import type { OperationItem } from '../types'
import { bocLabelKeyForOperation, textGenerateManifest } from '@foodlista/operations'
import { AVAILABLE_AI_FEATURES } from '@foodlista/shared/ai/types'
import { readOperationContext } from './read-operation-context'

export function formatOperationTypeFallback(type: string): string {
  return type
    .split('.')
    .map(part => part.replace(/[-_]/g, ' '))
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' · ')
}

/**
 * `text.generate` serves every AI text feature (translate, improve, …) under a
 * single operation type — one Trigger task, one ability, one context spec —
 * so the type alone would label five visibly different actions identically.
 * The title is refined by the `feature` already projected on the envelope
 * context, and only for a feature this build knows: the context is persisted
 * data, so an unrecognized value falls back to the generic label instead of
 * addressing an i18n key derived from it.
 *
 * Every lookup is `te`-probed, so a missing translation, an absent context
 * (envelope predating the column, or a run in flight across a deploy) and a
 * version mismatch all degrade to the generic label rather than rendering a
 * raw key.
 */
export function operationTypeLabel(
  item: Pick<OperationItem, 'type' | 'contextVersion' | 'context'>,
  translate: (key: string) => string,
  hasTranslation: (key: string) => boolean,
): string {
  if (item.type === textGenerateManifest.type) {
    const feature = readOperationContext(item)?.feature
    const variantKey = typeof feature === 'string'
      && (AVAILABLE_AI_FEATURES as readonly string[]).includes(feature)
      ? `operations.boc.features.textGenerate.${feature}`
      : undefined

    if (variantKey && hasTranslation(variantKey)) {
      return translate(variantKey)
    }
  }

  const key = bocLabelKeyForOperation(item.type)
  if (key && hasTranslation(key)) {
    return translate(key)
  }

  return formatOperationTypeFallback(item.type)
}
